import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ShareDetailsModal = ({ share, isOpen, onClose, onRevoke, onExtend }) => {
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  if (!isOpen || !share) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success bg-success/10';
      case 'expired': return 'text-error bg-error/10';
      case 'pending': return 'text-warning bg-warning/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const mockAuditLogs = [
    {
      id: 1,
      action: 'Share Created',
      timestamp: '2025-01-03T10:30:00Z',
      user: 'Admin User',
      details: 'Share created with viewer permissions'
    },
    {
      id: 2,
      action: 'First Access',
      timestamp: '2025-01-03T11:15:00Z',
      user: share?.recipientName,
      details: 'Recipient accessed shared items for the first time'
    },
    {
      id: 3,
      action: 'Item Viewed',
      timestamp: '2025-01-03T14:22:00Z',
      user: share?.recipientName,
      details: 'Viewed AWS Production Keys'
    }
  ];

  const mockSharedItems = [
    {
      id: 1,
      name: 'AWS Production Keys',
      type: 'API Keys',
      lastAccessed: '2025-01-03T14:22:00Z',
      accessCount: 5
    },
    {
      id: 2,
      name: 'Database Credentials',
      type: 'Login',
      lastAccessed: '2025-01-02T16:45:00Z',
      accessCount: 3
    }
  ];

  const handleRevoke = () => {
    onRevoke(share?.id);
    setShowRevokeDialog(false);
    onClose();
  };

  const tabs = [
    { id: 'details', label: 'Details', icon: 'Info' },
    { id: 'items', label: 'Shared Items', icon: 'Key' },
    { id: 'activity', label: 'Activity Log', icon: 'Activity' }
  ];

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-300 p-4">
        <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center space-x-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                share?.type === 'internal' ? 'bg-primary/10' : 'bg-accent/10'
              }`}>
                <Icon 
                  name={share?.type === 'internal' ? 'Users' : 'ExternalLink'} 
                  size={24} 
                  className={share?.type === 'internal' ? 'text-primary' : 'text-accent'}
                />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">{share?.recipientName}</h2>
                <p className="text-sm text-muted-foreground">{share?.recipientEmail}</p>
                <div className="flex items-center space-x-2 mt-1">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(share?.status)}`}>
                    {share?.status}
                  </span>
                  <span className="text-xs text-muted-foreground capitalize">
                    {share?.accessLevel} access
                  </span>
                </div>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>

          {/* Tabs */}
          <div className="border-b border-border">
            <div className="flex space-x-8 px-6">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 py-4 border-b-2 transition-smooth ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span className="font-medium">{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-96">
            {activeTab === 'details' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Share Type</label>
                      <p className="text-foreground capitalize">{share?.type} sharing</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Access Level</label>
                      <p className="text-foreground capitalize">{share?.accessLevel}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Created</label>
                      <p className="text-foreground">{new Date(share.createdAt)?.toLocaleString()}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Created By</label>
                      <p className="text-foreground">Admin User</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Expiration</label>
                      <p className="text-foreground">
                        {share?.expiresAt ? new Date(share.expiresAt)?.toLocaleString() : 'No expiration'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Usage Limit</label>
                      <p className="text-foreground">
                        {share?.usageLimit ? `${share?.usageCount}/${share?.usageLimit} times` : 'Unlimited'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Last Access</label>
                      <p className="text-foreground">
                        {share?.lastAccess ? new Date(share.lastAccess)?.toLocaleString() : 'Never'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Total Items</label>
                      <p className="text-foreground">{share?.itemCount} items</p>
                    </div>
                  </div>
                </div>

                {share?.type === 'external' && (
                  <div className="mt-6 p-4 bg-muted rounded-lg">
                    <h4 className="font-medium text-foreground mb-2">External Share Settings</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Passphrase Protected:</span>
                        <span className="text-foreground">Yes</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Domain Restrictions:</span>
                        <span className="text-foreground">company.com</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'items' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground">Shared Items</h3>
                <div className="space-y-3">
                  {mockSharedItems?.map((item) => (
                    <div key={item?.id} className="flex items-center justify-between p-4 bg-muted rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Icon name="Key" size={20} className="text-primary" />
                        </div>
                        <div>
                          <h4 className="font-medium text-foreground">{item?.name}</h4>
                          <p className="text-sm text-muted-foreground">{item?.type}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-foreground">{item?.accessCount} accesses</p>
                        <p className="text-xs text-muted-foreground">
                          Last: {new Date(item.lastAccessed)?.toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground">Activity Log</h3>
                <div className="space-y-4">
                  {mockAuditLogs?.map((log) => (
                    <div key={log?.id} className="flex items-start space-x-4 p-4 bg-muted rounded-lg">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name="Activity" size={16} className="text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-medium text-foreground">{log?.action}</h4>
                          <span className="text-xs text-muted-foreground">
                            {new Date(log.timestamp)?.toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-1">{log?.details}</p>
                        <p className="text-xs text-muted-foreground">by {log?.user}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-border">
            <div className="flex items-center space-x-3">
              {share?.status === 'active' && (
                <>
                  <Button variant="outline" onClick={() => onExtend(share?.id)}>
                    <Icon name="Clock" size={16} className="mr-2" />
                    Extend Access
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => setShowRevokeDialog(true)}
                  >
                    <Icon name="UserX" size={16} className="mr-2" />
                    Revoke Access
                  </Button>
                </>
              )}
            </div>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
      {/* Revoke Confirmation Dialog */}
      {showRevokeDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-400">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-error/10 rounded-full flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} className="text-error" />
              </div>
              <div>
                <h3 className="font-medium text-foreground">Revoke Share Access</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone</p>
              </div>
            </div>
            
            <p className="text-sm text-foreground mb-6">
              Are you sure you want to revoke access for <strong>{share?.recipientName}</strong>? 
              They will immediately lose access to all shared items.
            </p>
            
            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowRevokeDialog(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleRevoke}
              >
                Revoke Access
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareDetailsModal;